/**
 * One refresh and one retry when the server refuses a sign-in token with a 401.
 *
 * WHY THIS EXISTS (precondition P14 of the move, Q11). supabase-js renews the
 * token on a TIMER (a 30 s tick renews a token within 90 s of expiry) and never
 * looks at a 401. Today that is fine, because the server accepts every token the
 * timer has not yet got to. On move day the signing key changes: a token minted
 * just before the switch is refused by the new server until the timer notices —
 * up to an hour of "unauthenticated" from every function and every query, for a
 * bidder who did nothing wrong. One refresh gets them a token the new server
 * signed; this makes the client take it the moment it is told.
 *
 * WHAT IT COVERS: requests to the project's Data API (`/rest/v1/`, which is
 * every query and every RPC) and to Edge Functions (`/functions/v1/`, or the
 * separate functions address when one is set). Both are issued by supabase-js
 * OUTSIDE its auth lock, so calling back into the auth client here is safe.
 * Requests that go around the Supabase client (a native upload, our own
 * services) get the same rule through `retryToken` — see `session-request.ts`.
 *
 * 🔴 WHAT IT MUST NOT RETRY: anything under `/auth/v1/`. supabase-js issues
 * those requests from INSIDE its auth lock (`getUser()` holds it while it asks
 * `/auth/v1/user`). A nested `getSession()` / `refreshSession()` from here
 * queues behind the call that is waiting on this very request: a deadlock that
 * freezes every later request. Measured, not assumed — see the test "a 401 on
 * /auth/v1/user is passed through, not retried". `getUserRefreshing` below gives
 * `/auth/v1/user` the same retry from OUTSIDE the lock.
 *
 * THE REFRESH CALL ITSELF (`/auth/v1/token?grant_type=refresh_token`) is never
 * retried, but its ANSWER is read here — the refresh guard below — because
 * supabase-js 2.103.3 (auth-js 2.103.3) reads it in a way that hurts on move day:
 *  - it signs the person out on ANY failed refresh except a network error or a
 *    502 / 503 / 504 / 52x — so a 500 or a 429 (an overloaded auth server, a
 *    rate limit) ended a perfectly good sign-in;
 *  - for the failures it does keep, it retries with growing waits (200 ms
 *    doubling) for up to 30 s while HOLDING its auth lock, and every request
 *    waits on that lock first — about 25 s of a frozen app per failed refresh.
 * Those are its only two outcomes for a failure. The guard therefore answers a
 * refresh that could not happen (no answer, 5xx, 408, 429) with the session the
 * device already holds: nothing is lost, the lock is released at once, and the
 * saved expiry is pushed just far enough out that the next attempt waits
 * `cooldownMs`. Only a real refusal (any other 4xx: the refresh token is
 * invalid, revoked or already used) reaches supabase-js, which signs out. This
 * reads the saved session straight from storage; it never calls the auth
 * client, so it cannot deadlock.
 *
 * THE RULES
 *  - ONE retry per request, whatever the second answer is. No loop.
 *  - A 401 only. A 403 is "signed in, not allowed" — a new token cannot change it.
 *  - Only a token that belongs to the session. A request carrying the anon key
 *    (signed out) or a token this client never held (a caller's own header) is
 *    left alone: swapping it would impersonate the signed-in person.
 *  - 🔴 Only for the SAME PERSON. A token is never swapped for one that belongs
 *    to somebody else — on a shared device the next person signs in while the
 *    last person's request is still in flight, and that request must not be
 *    sent again under the new person's name. Every remembered token is
 *    forgotten at sign-out and when the person changes, and the token a retry
 *    would send must name the same person (`sub`) as the one that was refused.
 *  - Concurrent 401s share ONE refresh. A 401 that arrives after someone else
 *    already renewed the token just takes the new one — no second refresh.
 *  - A token we renewed to in the last five minutes that is refused again is
 *    not an expiry; a function that answers 401 for its own reasons must not
 *    cost a refresh per call (each one rotates the refresh token).
 *  - A refused refresh signs the person out. A refresh that could not happen
 *    (network, 5xx — the move's pause answers every non-GET under `/auth/v1/`
 *    with 503 — 408, 429) keeps the session, leaves the original 401 as the
 *    answer, and the next attempt waits `cooldownMs`.
 *
 * MONEY: a 401 here means the request was refused BEFORE it ran — PostgREST
 * checks the token before it opens a transaction, no SQL function in this
 * repository can emit a 401 itself, and every Edge Function that answers 401
 * does so at its sign-in gate, ahead of any database or Stripe call. `place_bid`
 * also carries an idempotency key, so even a request that had run would replay.
 * The engine's bid endpoint is NOT behind this wrapper (it has its own 401
 * handling in bidding.ts — see the report).
 */

import { isAuthApiError, isAuthRetryableFetchError } from '@supabase/supabase-js';

/** How long a refresh that could not happen (not one that was refused) is left alone before the next try. */
export const DEFAULT_REFRESH_COOLDOWN_MS = 10_000;

/**
 * How long a token we renewed to counts as "fresh". A 401 on a token that fresh
 * is not an expiry, so it buys no second refresh — but only for this long: a
 * token that is refused an hour on (a device clock running slow, a revoked
 * session) should get its refresh.
 */
const FRESH_TOKEN_MS = 5 * 60_000;

/** How many of the session's earlier tokens are remembered, to tell them from a caller's own. */
const REMEMBERED_TOKENS = 16;

/**
 * supabase-js counts a session as expired this long before it really is, and
 * refreshes it on the next `getSession()` (auth-js `EXPIRY_MARGIN_MS`: 3 ticks
 * of 30 s). A deferred refresh must leave the saved expiry beyond this, or every
 * request would ask for another refresh straight away.
 */
const SUPABASE_EXPIRY_MARGIN_MS = 90_000;

/**
 * The slice of the auth client this needs. Structural, so the real client fits
 * and a test can supply a stand-in without a network.
 */
export interface RefreshAuthPort {
  getSession(): Promise<{ data: { session: { access_token: string } | null } }>;
  refreshSession(): Promise<{
    data: { session: { access_token: string } | null };
    error: unknown;
  }>;
  signOut(options: { scope: 'local' }): Promise<unknown>;
}

/** The saved session as supabase-js writes it to storage. Only what the guard reads is typed. */
export interface StoredSession {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
  [field: string]: unknown;
}

export interface RefreshOn401Options {
  /** The fetch to wrap — in the client, the timeout + connection-reporting fetch. */
  inner: typeof fetch;
  /** The project URL. Data API and auth requests to it are the ones read here. */
  apiUrl: string;
  /** Where Edge Functions are called. Defaults to `<apiUrl>/functions/v1`. */
  functionsUrl?: string;
  /** The anon key: a request that carries it is signed out, and a refresh cannot help. */
  anonKey: string;
  /** The auth client. A function so it can be named before the client exists. */
  auth: () => RefreshAuthPort;
  /**
   * The session as saved on this device, read straight from storage (never via
   * the auth client: the refresh guard runs inside supabase-js's auth lock).
   * Without it a refresh that could not happen is reported to supabase-js as a
   * 503, which keeps the session but not the lock (the ~25 s wait).
   */
  readStoredSession?: () => Promise<StoredSession | null>;
  /** Wait after a refresh that could not happen (not one that was refused). Defaults to 10 s. */
  cooldownMs?: number;
  /** Injectable clock for tests. */
  now?: () => number;
}

export interface RefreshOn401 {
  /** Drop-in for `global.fetch`. */
  fetch: typeof fetch;
  /**
   * Tell it the session's token (call from `onAuthStateChange` with
   * `session?.access_token`). Without this a request that was in flight while
   * the timer renewed the token would look like a caller's own token and not be
   * retried. `null` means signed out: everything remembered is forgotten.
   */
  noteToken(token: string | null | undefined): void;
  /**
   * The token to send again after `sent` was refused with a 401, or null when a
   * retry cannot help — the same decision the transport makes, for callers that
   * go around the Supabase client. Never a token of somebody else's. A plain
   * function (it is handed around unbound).
   */
  retryToken: (sent: string) => Promise<string | null>;
}

/** The two places the transport retries — see the header for why `/auth/v1/` is absent. */
function retryablePrefixes(apiBase: string, functionsBase: string): string[] {
  return [`${apiBase}/rest/v1/`, `${functionsBase}/`];
}

function bearerOf(headers: Headers): string | null {
  const value = headers.get('authorization');
  const match = value === null ? null : /^Bearer\s+(.+)$/i.exec(value.trim());
  return match?.[1] ?? null;
}

/**
 * The person a token was issued to: its `sub` claim, read WITHOUT checking the
 * signature. Good enough to tell two locally held tokens apart, which is all it
 * is used for; it grants nothing. Null for anything that is not a token.
 */
export function personOfToken(token: string | null | undefined): string | null {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const base64 =
      payload.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (payload.length % 4)) % 4);
    const claims = JSON.parse(atob(base64)) as { sub?: unknown };
    return typeof claims.sub === 'string' && claims.sub !== '' ? claims.sub : null;
  } catch {
    return null;
  }
}

/** A body that can be sent a second time. A stream is consumed by the first attempt. */
export function isReplayable(body: RequestInit['body']): boolean {
  if (body === undefined || body === null) return true;
  return !(typeof ReadableStream !== 'undefined' && body instanceof ReadableStream);
}

/** Free the first answer's connection once we know it will not be read. Never throws. */
export function discard(response: Response): void {
  try {
    void response.body?.cancel().catch(() => undefined);
  } catch {
    // A body that is already locked or used has nothing to release.
  }
}

/** The refresh server could not answer — as opposed to refusing. */
function couldNotRefresh(status: number): boolean {
  return status >= 500 || status === 408 || status === 429;
}

function refreshTokenIn(body: RequestInit['body']): string | null {
  if (typeof body !== 'string') return null;
  try {
    const parsed = JSON.parse(body) as { refresh_token?: unknown };
    return typeof parsed.refresh_token === 'string' ? parsed.refresh_token : null;
  } catch {
    return null;
  }
}

export function createRefreshOn401(options: RefreshOn401Options): RefreshOn401 {
  const { inner, anonKey } = options;
  const cooldownMs = options.cooldownMs ?? DEFAULT_REFRESH_COOLDOWN_MS;
  const now = options.now ?? Date.now;
  const apiBase = options.apiUrl.replace(/\/+$/, '');
  const functionsBase = (options.functionsUrl ?? `${apiBase}/functions/v1`).replace(/\/+$/, '');
  const prefixes = retryablePrefixes(apiBase, functionsBase);

  const known = new Set<string>();
  // Whose tokens `known` holds. A different person means a different set.
  let knownPerson: string | null = null;
  let inflight: Promise<string | null> | null = null;
  // The token our own refresh produced most recently. A 401 on THIS token is not
  // an expiry, so it does not buy another refresh.
  let lastRenewedTo: string | null = null;
  let lastRenewedAt: number | null = null;
  // When a refresh last could not happen (any path: ours, the timer, a load).
  let blockedAt: number | null = null;

  /**
   * Is `since` within the last `ms`? A clock that has been set BACKWARDS since
   * (a network time correction) makes the elapsed time negative; that counts as
   * long ago, or one correction would hold every guard shut for as long as the
   * clock jumped.
   */
  function within(since: number | null, ms: number): boolean {
    if (since === null) return false;
    const elapsed = now() - since;
    return elapsed >= 0 && elapsed < ms;
  }

  /** Signed out, or someone else signed in: nothing of the last person's may be reused. */
  function forget(): void {
    known.clear();
    knownPerson = null;
    lastRenewedTo = null;
    lastRenewedAt = null;
  }

  function noteToken(token: string | null | undefined): void {
    if (!token) {
      forget();
      return;
    }
    const person = personOfToken(token);
    if (person !== knownPerson) {
      forget();
      knownPerson = person;
    }
    known.delete(token);
    known.add(token);
    if (known.size > REMEMBERED_TOKENS) {
      const oldest = known.values().next().value;
      if (oldest !== undefined) known.delete(oldest);
    }
  }

  /** A refresh that was refused: the refresh token is no good, so neither is the sign-in. */
  async function afterRefusedRefresh(): Promise<void> {
    // supabase-js has already dropped the session and announced SIGNED_OUT (it
    // does so for every refusal). Check rather than trust it: if a session
    // survived, end it here.
    try {
      const auth = options.auth();
      const { data } = await auth.getSession();
      if (data.session !== null) await auth.signOut({ scope: 'local' });
    } catch {
      // Signing out is best-effort here; the refusal itself is already recorded.
    }
  }

  /** `refused` is the live token the server just turned down. */
  async function renew(refused: string): Promise<string | null> {
    let result: Awaited<ReturnType<RefreshAuthPort['refreshSession']>>;
    try {
      result = await options.auth().refreshSession();
    } catch {
      // Thrown rather than returned: no answer at all. Keep the session.
      blockedAt = now();
      return null;
    }
    if (result.error) {
      const error = result.error;
      if (
        isAuthRetryableFetchError(error) ||
        (isAuthApiError(error) && couldNotRefresh(error.status))
      ) {
        // Could not happen (the guard normally absorbs these before supabase-js
        // sees them). Keep the session, leave the 401 as the answer, wait.
        blockedAt = now();
      } else if (isAuthApiError(error) && error.status >= 400 && error.status < 500) {
        await afterRefusedRefresh();
      }
      return null;
    }
    const token = result.data.session?.access_token ?? null;
    // The same token back is the refresh guard keeping the session through an
    // outage: nothing was renewed, so there is nothing new to retry with.
    if (token === null || token === refused) return null;
    noteToken(token);
    lastRenewedTo = token;
    lastRenewedAt = now();
    return token;
  }

  /** At most one refresh in flight; everyone who needs one waits on it. */
  function renewShared(refused: string): Promise<string | null> {
    inflight ??= renew(refused).finally(() => {
      inflight = null;
    });
    return inflight;
  }

  /** The decision, before the same-person check on its result. */
  async function decide(sent: string, person: string): Promise<string | null> {
    let current: string | null;
    try {
      // supabase-js renews an expired session here, so a late timer is handled too.
      current = (await options.auth().getSession()).data.session?.access_token ?? null;
    } catch {
      return null;
    }
    if (current === null) return null; // signed out meanwhile
    // Someone else is signed in now: their sign-in is not ours to spend.
    if (personOfToken(current) !== person) return null;
    noteToken(current);

    // Not the session's token — a caller's own header. Never swap it.
    if (sent !== current && !known.has(sent)) return null;

    // Someone (the timer, another request) already renewed it: take that token.
    if (sent !== current) return current;

    // The live token was refused. A refresh helps only if this is an expiry; a
    // token we renewed to a moment ago being refused again is not.
    if (sent === lastRenewedTo && within(lastRenewedAt, FRESH_TOKEN_MS)) return null;
    if (within(blockedAt, cooldownMs)) return null;
    return renewShared(sent);
  }

  async function retryToken(sent: string): Promise<string | null> {
    const person = personOfToken(sent);
    if (person === null) return null;
    const fresh = await decide(sent, person);
    // 🔴 Checked again on the way out: a refresh that was in flight while the
    // person changed answers with the NEW person's token.
    if (fresh === null || personOfToken(fresh) !== person) return null;
    return fresh;
  }

  /**
   * The session this device already holds, as a refresh answer — when the
   * request was refreshing exactly that session. Its saved expiry is moved to
   * just past supabase-js's margin plus the cooldown, so the next refresh
   * attempt (the timer's, or a request's) comes after the cooldown, not on the
   * very next request.
   */
  async function keepHeldSession(refreshToken: string | null): Promise<Response | null> {
    if (refreshToken === null || options.readStoredSession === undefined) return null;
    let held: StoredSession | null;
    try {
      held = await options.readStoredSession();
    } catch {
      return null;
    }
    if (held?.refresh_token !== refreshToken || typeof held.access_token !== 'string') return null;
    const nowSeconds = Math.floor(now() / 1000);
    const notBefore = Math.ceil((now() + SUPABASE_EXPIRY_MARGIN_MS + cooldownMs) / 1000);
    const expiresAt = Math.max(
      typeof held.expires_at === 'number' ? held.expires_at : 0,
      notBefore,
    );
    // supabase-js reads a session only when `expires_in` is truthy: never 0.
    const body = {
      ...held,
      expires_at: expiresAt,
      expires_in: Math.max(1, expiresAt - nowSeconds),
    };
    return new Response(JSON.stringify(body), {
      status: 200,
      headers: { 'content-type': 'application/json' },
    });
  }

  /** An answer supabase-js treats as "try again later", which keeps the session. */
  function unavailable(from?: Response): Response {
    return new Response(from?.body ?? JSON.stringify({ message: 'Sign-in refresh unavailable' }), {
      status: 503,
      statusText: 'Service Unavailable',
      headers: from?.headers ?? { 'content-type': 'application/json' },
    });
  }

  /** The refresh guard — see the header. */
  async function guardRefresh(
    input: Parameters<typeof fetch>[0],
    init: RequestInit | undefined,
  ): Promise<Response> {
    const refreshToken = refreshTokenIn(init?.body);
    if (within(blockedAt, cooldownMs)) {
      // It failed a moment ago: do not ask again yet (supabase-js's own retry
      // loop would otherwise send one every few hundred ms).
      return (await keepHeldSession(refreshToken)) ?? unavailable();
    }
    let response: Response;
    try {
      response = await inner(input, init);
    } catch (cause) {
      blockedAt = now();
      const kept = await keepHeldSession(refreshToken);
      if (kept !== null) return kept;
      throw cause; // supabase-js reads a thrown fetch as "try again later" already
    }
    if (response.ok) {
      blockedAt = null;
      return response;
    }
    // A refusal: the refresh token is no good. supabase-js signs out, rightly.
    if (!couldNotRefresh(response.status)) return response;
    blockedAt = now();
    const kept = await keepHeldSession(refreshToken);
    if (kept !== null) {
      discard(response);
      return kept;
    }
    return unavailable(response);
  }

  function urlOf(input: Parameters<typeof fetch>[0]): string | null {
    return typeof input === 'string' ? input : input instanceof URL ? input.href : null;
  }

  function isRefreshCall(url: string, init: RequestInit | undefined): boolean {
    if ((init?.method ?? 'GET').toUpperCase() !== 'POST') return false;
    if (!url.startsWith(`${apiBase}/auth/v1/token?`)) return false;
    try {
      return new URL(url).searchParams.get('grant_type') === 'refresh_token';
    } catch {
      return false;
    }
  }

  const guarded: typeof fetch = async (input, init) => {
    // Plain URL requests only: a `Request` object's body may already be spent.
    const url = urlOf(input);
    if (url !== null && isRefreshCall(url, init)) return guardRefresh(input, init);

    const response = await inner(input, init);
    if (response.status !== 401) return response;

    if (url === null) return response;
    if (!prefixes.some((prefix) => url.startsWith(prefix))) return response;
    if (init?.signal?.aborted === true || !isReplayable(init?.body)) return response;

    const headers = new Headers(init?.headers);
    const sent = bearerOf(headers);
    if (sent === null || sent === anonKey) return response;

    const fresh = await retryToken(sent);
    if (fresh === null) return response;

    headers.set('authorization', `Bearer ${fresh}`);
    discard(response);
    // The second answer is final, whatever it is.
    return inner(input, { ...init, headers });
  };

  return { fetch: guarded, noteToken, retryToken };
}

/**
 * `auth.getUser()` with the same one refresh, one retry on a 401.
 *
 * It lives here, outside the transport, because `getUser()` holds the auth lock
 * while it asks `/auth/v1/user` — a retry from inside that request would
 * deadlock (see the header). Called from the app, after `getUser()` has
 * returned, `refreshSession()` takes the lock normally.
 *
 * Only for the signed-in person's own token (no `jwt` argument): a token the
 * caller passes in is theirs to renew.
 */
export async function getUserRefreshing<
  Auth extends {
    getUser(): Promise<{ error: unknown }>;
    refreshSession(): Promise<{ error: unknown }>;
  },
>(auth: Auth): Promise<Awaited<ReturnType<Auth['getUser']>>> {
  const first = (await auth.getUser()) as Awaited<ReturnType<Auth['getUser']>>;
  const failure = (first as { error: unknown }).error;
  if (!isAuthApiError(failure) || failure.status !== 401) return first;
  const renewed = await auth.refreshSession();
  if (renewed.error) return first;
  return (await auth.getUser()) as Awaited<ReturnType<Auth['getUser']>>;
}
